import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  ExternalLink, CreditCard, QrCode, Loader2, ChevronRight, Download,
  ArrowLeft, Upload, CheckCircle2, Wallet,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { isNativeApp } from "@/lib/platform";
import ContinueOnWebCard from "@/components/ContinueOnWebCard";

const MAX_PROOF_BYTES = 5 * 1024 * 1024;
const ALLOWED_MIMES = ["image/jpeg", "image/jpg", "image/png", "application/pdf"];

/**
 * Multi-step renewal flow:
 *  Step 1 (picker) — list all enabled payment options (PayMongo + custom systems).
 *                    Native iOS shows a Continue-on-Web card only (Reader App).
 *  Step 2 (detail) — show the picked system's QR / link / instructions,
 *                    Download QR button, and a submission form for
 *                    reference number, amount paid, and proof-of-payment upload.
 *
 * On submit → POST /api/student/course-renewals which lands the payment in
 * the admin Payments module as a pending renewal.
 */
export default function RenewSubscriptionModal({ open, course, onClose, onSubmitted }) {
  const [paymongo, setPaymongo] = useState(null);
  const [systems, setSystems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // "paymongo" | system.id | null
  const [form, setForm] = useState({ ref: "", amount: "", file: null, notes: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelected(null);
    setForm({ ref: "", amount: "", file: null, notes: "" });
    if (isNativeApp()) { setLoading(false); return; }
    setLoading(true);
    Promise.all([
      api.get("/settings/payment/public").catch(() => ({ data: null })),
      api.get("/payment-systems/public").catch(() => ({ data: [] })),
    ]).then(([p, s]) => {
      setPaymongo(p.data || null);
      setSystems(s.data || []);
    }).finally(() => setLoading(false));
  }, [open]);

  const selectedSystem = useMemo(() => {
    if (!selected || selected === "paymongo") return null;
    return systems.find((s) => s.id === selected) || null;
  }, [selected, systems]);

  const validateFile = (file) => {
    if (!file) return null;
    if (!ALLOWED_MIMES.includes(file.type)) { toast.error("Only JPG, PNG, or PDF files are allowed"); return null; }
    if (file.size > MAX_PROOF_BYTES) { toast.error("File must be 5MB or smaller"); return null; }
    return file;
  };

  const downloadQr = () => {
    const url = selectedSystem?.qr_code_data_url;
    if (!url) return;
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(selectedSystem.name || "payment").replace(/\s+/g, "-").toLowerCase()}-qr.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const submit = async () => {
    if (!course?.id) { toast.error("Missing course reference"); return; }
    if (!selected) { toast.error("Choose a payment method"); return; }
    if (!form.ref.trim() && !form.file) {
      toast.error("Provide a reference number or upload proof of payment");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("course_id", course.id);
      if (selected !== "paymongo") fd.append("payment_system_id", selected);
      fd.append("payment_reference_number", form.ref.trim().slice(0, 80));
      if (form.amount) fd.append("amount_paid", String(form.amount).trim());
      if (form.file) fd.append("proof_of_payment", form.file);
      if (form.notes) fd.append("notes", form.notes.trim().slice(0, 600));
      await api.post("/student/course-renewals", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Renewal payment submitted. Waiting for admin verification.", { duration: 6000 });
      onSubmitted?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit renewal payment");
    } finally {
      setBusy(false);
    }
  };

  const paymongoEnabled = paymongo?.paymongo_enabled && paymongo?.paymongo_payment_link;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="renew-subscription-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-indigo-600" />
            Renew {course?.course_name || "Subscription"}
          </DialogTitle>
        </DialogHeader>

        {isNativeApp() ? (
          <ContinueOnWebCard
            title="Renew on our website"
            message="Sign in to prepmasters.ph with the same email to see all available payment methods (GCash, Maya, bank transfer, and more) and renew your access."
            path="/payment-instructions"
            ctaLabel="View Payment Options"
          />
        ) : loading ? (
          <div className="flex items-center gap-2 text-slate-500 py-8 justify-center">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading payment options…
          </div>
        ) : !selected ? (
          <div className="space-y-3" data-testid="renew-picker">
            <p className="text-sm text-slate-600 dark:text-slate-300">
              Choose your payment method. You'll see the payment details on the next step and can upload
              your proof of payment right here.
            </p>

            {paymongoEnabled && (
              <button
                type="button"
                onClick={() => setSelected("paymongo")}
                className="w-full text-left"
                data-testid="renew-pick-paymongo"
              >
                <Card className="p-4 border-emerald-200 hover:border-emerald-400 hover:shadow-sm transition-all cursor-pointer bg-emerald-50/40">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-md bg-emerald-600 grid place-items-center">
                      <Wallet className="w-5 h-5 text-white" strokeWidth={2} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-emerald-900">PayMongo (Card / GCash)</div>
                      <div className="text-xs text-slate-600">Instant online checkout — you'll pay on their page and then upload proof here.</div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                  </div>
                </Card>
              </button>
            )}

            {systems.length === 0 && !paymongoEnabled && (
              <Card className="p-4 border-amber-200 bg-amber-50">
                <p className="text-sm text-amber-900">
                  No payment methods are enabled yet. Please contact your administrator.
                </p>
              </Card>
            )}

            {systems.map((ps) => (
              <button
                type="button"
                key={ps.id}
                onClick={() => setSelected(ps.id)}
                className="w-full text-left"
                data-testid={`renew-pick-system-${ps.id}`}
              >
                <Card className="p-4 border-slate-200 dark:border-slate-700 hover:border-indigo-400 hover:shadow-sm transition-all cursor-pointer">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-md border border-slate-200 dark:border-slate-700 grid place-items-center bg-white dark:bg-slate-900 overflow-hidden shrink-0">
                      {ps.logo_data_url ? (
                        <img src={ps.logo_data_url} alt={`${ps.name} logo`} className="w-full h-full object-contain" />
                      ) : ps.qr_code_data_url ? (
                        <img src={ps.qr_code_data_url} alt="" className="w-full h-full object-contain" />
                      ) : (
                        <QrCode className="w-5 h-5 text-slate-400" strokeWidth={1.5} />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-900 dark:text-slate-100">{ps.name}</div>
                      {(ps.account_name || ps.account_number) && (
                        <div className="text-[12px] text-slate-500 dark:text-slate-400 mt-0.5 truncate">
                          {ps.account_name}{ps.account_name && ps.account_number ? " · " : ""}
                          {ps.account_number && <span className="font-mono">{ps.account_number}</span>}
                        </div>
                      )}
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                  </div>
                </Card>
              </button>
            ))}
          </div>
        ) : (
          <div className="space-y-3" data-testid="renew-detail">
            <button
              type="button"
              onClick={() => setSelected(null)}
              className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900"
              data-testid="renew-back-btn"
            >
              <ArrowLeft className="w-3 h-3" /> Back to payment methods
            </button>

            {selected === "paymongo" ? (
              <Card className="p-4 border-emerald-200 bg-emerald-50/60">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-md bg-emerald-600 grid place-items-center">
                    <Wallet className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-emerald-900">Pay via PayMongo</div>
                    <div className="text-xs text-slate-600">Card or GCash — checkout opens in a new tab.</div>
                  </div>
                </div>
                <a
                  href={paymongo.paymongo_payment_link}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="renew-open-paymongo"
                >
                  <Button className="w-full mt-3 bg-emerald-600 hover:bg-emerald-700 text-white">
                    Open PayMongo Checkout <ExternalLink className="w-3.5 h-3.5 ml-2" />
                  </Button>
                </a>
                {paymongo?.payment_instructions && (
                  <p className="text-[12px] text-emerald-900 mt-3 whitespace-pre-line">
                    {paymongo.payment_instructions}
                  </p>
                )}
              </Card>
            ) : selectedSystem ? (
              <Card className="p-4 border-slate-200 dark:border-slate-700">
                <div className="flex items-start gap-3">
                  <div className="w-16 h-16 rounded-md border border-slate-200 dark:border-slate-700 grid place-items-center bg-white dark:bg-slate-900 overflow-hidden shrink-0">
                    {selectedSystem.logo_data_url ? (
                      <img src={selectedSystem.logo_data_url} alt={`${selectedSystem.name} logo`} className="w-full h-full object-contain" data-testid="renew-detail-logo" />
                    ) : (
                      <QrCode className="w-8 h-8 text-slate-400" strokeWidth={1.5} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-900 dark:text-slate-100 text-base">{selectedSystem.name}</div>
                    {(selectedSystem.account_name || selectedSystem.account_number) && (
                      <div className="text-[12px] text-slate-600 dark:text-slate-300 mt-0.5">
                        {selectedSystem.account_name && (<><span className="font-medium">Account:</span> {selectedSystem.account_name}</>)}
                        {selectedSystem.account_name && selectedSystem.account_number && <span className="mx-1 text-slate-400">·</span>}
                        {selectedSystem.account_number && (<span className="font-mono">{selectedSystem.account_number}</span>)}
                      </div>
                    )}
                    <div className="mt-2 flex flex-wrap gap-2">
                      {selectedSystem.qr_code_data_url && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={downloadQr}
                          className="h-7"
                          data-testid="renew-download-qr"
                        >
                          <Download className="w-3 h-3 mr-1" /> Download QR
                        </Button>
                      )}
                      {selectedSystem.link && (
                        <a
                          href={selectedSystem.link}
                          target="_blank"
                          rel="noreferrer"
                          data-testid="renew-open-link"
                        >
                          <Button size="sm" variant="outline" className="h-7">
                            <ExternalLink className="w-3 h-3 mr-1" /> Open payment link
                          </Button>
                        </a>
                      )}
                    </div>
                  </div>
                </div>

                {selectedSystem.qr_code_data_url && (
                  <div className="mt-4 flex flex-col items-center bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md p-4" data-testid="renew-detail-qr-panel">
                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-2 flex items-center gap-1">
                      <QrCode className="w-3 h-3" /> Scan to pay
                    </div>
                    <img
                      src={selectedSystem.qr_code_data_url}
                      alt={`${selectedSystem.name} QR code`}
                      className="w-56 h-56 sm:w-64 sm:h-64 object-contain"
                      data-testid="renew-detail-qr"
                    />
                  </div>
                )}

                {selectedSystem.instructions && (
                  <div className="text-[12px] text-slate-700 dark:text-slate-200 mt-3 whitespace-pre-line bg-slate-50 dark:bg-slate-800/40 p-3 rounded border border-slate-200 dark:border-slate-700">
                    {selectedSystem.instructions}
                  </div>
                )}
              </Card>
            ) : null}

            {/* Proof submission form */}
            <div className="space-y-3 pt-1">
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-2">
                <CheckCircle2 className="w-3 h-3" /> After paying, fill in your details
              </div>
              <div>
                <Label htmlFor="renew-ref">Payment Reference Number</Label>
                <Input
                  id="renew-ref"
                  value={form.ref}
                  onChange={(e) => setForm({ ...form, ref: e.target.value })}
                  placeholder="e.g., 4231 0987 2233"
                  className="font-mono mt-1.5"
                  data-testid="renew-ref-input"
                />
              </div>
              <div>
                <Label htmlFor="renew-amount">Amount Paid (PHP)</Label>
                <Input
                  id="renew-amount"
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  min="0"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  placeholder="e.g., 1500"
                  className="mt-1.5"
                  data-testid="renew-amount-input"
                />
              </div>
              <div>
                <Label htmlFor="renew-proof">Upload Proof of Payment</Label>
                <Input
                  id="renew-proof"
                  type="file"
                  accept="image/jpeg,image/png,application/pdf"
                  onChange={(e) => setForm({ ...form, file: validateFile(e.target.files?.[0] || null) })}
                  className="mt-1.5 cursor-pointer file:mr-2 file:rounded-md file:border-0 file:bg-slate-100 file:px-2 file:py-1.5 file:text-xs file:font-semibold file:text-slate-700"
                  data-testid="renew-proof-input"
                />
                <p className="text-[11px] text-slate-500 mt-1">JPG, PNG, or PDF (max 5MB). Either reference number or proof file is required.</p>
                {form.file && (
                  <div className="text-xs text-emerald-700 mt-1 flex items-center gap-1"><Upload className="w-3 h-3" /> {form.file.name}</div>
                )}
              </div>
              <div>
                <Label htmlFor="renew-notes">Note to admin <span className="text-slate-400 text-xs ml-1">optional ({form.notes.length}/600)</span></Label>
                <Textarea
                  id="renew-notes"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value.slice(0, 600) })}
                  placeholder="Anything else the admin should know?"
                  rows={2}
                  data-testid="renew-notes-input"
                />
              </div>
            </div>

            <div className="pt-1 flex justify-end gap-2">
              <Button variant="outline" onClick={onClose} disabled={busy} data-testid="renew-close-btn">
                Cancel
              </Button>
              <Button
                onClick={submit}
                disabled={busy}
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
                data-testid="renew-submit-btn"
              >
                {busy ? (<><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Submitting…</>) : "Submit Renewal Payment"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
